import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import PageLoader from "../../components/common/PageLoader";
import api from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import "./UpgradePlans.css";

const formatMoney = (amount) => `Rs. ${Number(amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

function UpgradePlanDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [payload, setPayload] = useState(null);

  const ensureRazorpayScript = async () => {
    if (window.Razorpay) return true;

    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchDetails = useCallback(async () => {
    try {
      const response = await api.get(`/upgrade/plans/${id}`);
      setPayload(response.data?.data || null);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Unable to load plan",
        text: error.response?.data?.message || "Please try again.",
      }).then(() => navigate("/upgrade"));
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    if (user && !user.is_owner) {
      navigate("/dashboard", { replace: true });
      return;
    }

    fetchDetails();
  }, [id, user, navigate, fetchDetails]);

  const verifySubscription = async (razorpaySubscriptionId) => {
    const response = await api.post("/upgrade/plans/verify-subscription", {
      razorpay_subscription_id: razorpaySubscriptionId,
    });

    await refreshUser();

    Swal.fire({
      icon: "success",
      title: "Plan upgraded",
      text: response.data?.message || "Subscription activated successfully.",
      timer: 1800,
      showConfirmButton: false,
    });

    navigate("/upgrade");
  };

  const handlePayNow = async () => {
    if (!payload?.plan) return;

    setSubmitting(true);

    try {
      const response = await api.post("/upgrade/plans/create-subscription", {
        plan_id: payload.plan.id,
      });

      if (response.data?.type === "free") {
        await refreshUser();

        Swal.fire({
          icon: "success",
          title: "Plan activated",
          text: response.data?.message || "Free plan activated successfully.",
          timer: 1800,
          showConfirmButton: false,
        });

        navigate("/upgrade");
        return;
      }

      const razorpayLoaded = await ensureRazorpayScript();
      if (!razorpayLoaded) {
        throw new Error("Razorpay SDK failed to load.");
      }

      const checkoutPayload = response.data?.data || {};

      const options = {
        key: checkoutPayload.razorpay_key,
        subscription_id: checkoutPayload.subscription_id,
        name: user?.companyDetails?.company_name || checkoutPayload.plan?.name || "Cedur Plan",
        description: checkoutPayload.plan?.name || "Cedur Subscription",
        handler: async (rzpResponse) => {
          try {
            await verifySubscription(rzpResponse.razorpay_subscription_id);
          } catch (verifyError) {
            Swal.fire({
              icon: "error",
              title: "Verification failed",
              text: verifyError.response?.data?.message || "Unable to verify subscription.",
            });
          }
        },
        modal: {
          ondismiss: () => {
            Swal.fire({
              icon: "info",
              title: "Payment not completed",
              text: "You can resume the upgrade whenever you're ready.",
            });
          },
        },
        prefill: {
          name: `${user?.first_name || ""} ${user?.last_name || ""}`.trim(),
          email: user?.email || "",
          contact: user?.phone_number || "",
        },
        theme: {
          color: "#635bff",
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Payment setup failed",
        text: error.response?.data?.message || error.message || "Unable to start the upgrade payment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <MainLayout><PageLoader force copy="Loading upgrade details..." /></MainLayout>;
  }

  if (!payload?.plan) {
    return null;
  }

  const { plan, payable_summary: summary, usage_summary: usage } = payload;

  return (
    <MainLayout>
      <div className="upgrade-page">
        <div className="upgrade-detail-shell">
          <div className="upgrade-detail-header">
            <div>
              <span className="upgrade-kicker">Pricing & Subscription</span>
              <h2>Upgrade Plan</h2>
              <p>Review included seats, unlimited onboarding, add-on pricing, and your current payable estimate before checkout.</p>
            </div>
            <button type="button" className="upgrade-history-link" onClick={() => navigate("/plan-history")}>
              Payment History
            </button>
          </div>

          <div className="upgrade-detail-card">
            <div className="upgrade-detail-grid">
              <div className="upgrade-detail-primary">
                <div className="upgrade-stat-row">
                  <div className="upgrade-stat-card">
                    <h4>New Plan</h4>
                    <p>{plan.plan_name}</p>
                  </div>
                  <div className="upgrade-stat-card wide">
                    <h4>Plan Details</h4>
                    <p>{plan.plan_details || "No additional description available."}</p>
                  </div>
                </div>

                <div className="upgrade-stat-row">
                  <div className="upgrade-stat-card">
                    <h4>Base Fee</h4>
                    <p>{formatMoney(plan.base_fee)}</p>
                  </div>
                  <div className="upgrade-stat-card">
                    <h4>Included Employees</h4>
                    <p>{plan.included_employees || 0}</p>
                  </div>
                  <div className="upgrade-stat-card">
                    <h4>Add-on Rate</h4>
                    <p>{formatMoney(plan.add_on_rate)}</p>
                  </div>
                </div>

                <div className="upgrade-stat-row">
                  <div className="upgrade-stat-card">
                    <h4>Current Active Employees</h4>
                    <p>{usage?.current_employee_count || 0}</p>
                  </div>
                  <div className="upgrade-stat-card">
                    <h4>Additional Employees</h4>
                    <p>{usage?.additional_employees || 0}</p>
                  </div>
                  <div className="upgrade-stat-card">
                    <h4>Employee Onboarding</h4>
                    <p>Unlimited</p>
                  </div>
                </div>

                <div className="upgrade-stat-card full">
                  <h4>Plan Features</h4>
                  <p>{plan.features_count || 0} total</p>

                  <div className="upgrade-detail-features">
                    {(plan.visible_features || []).map((feature) => (
                      <span key={feature} className="upgrade-feature-chip">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <aside className="upgrade-summary-card">
                <div className="upgrade-summary-note">
                  Charges use the highest active employee count in the billing cycle.
                </div>
                <table>
                  <tbody>
                    <tr>
                      <th>Old Plan</th>
                      <td>{summary?.old_plan || "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Months Remaining</th>
                      <td>{summary?.months_remaining ?? "N/A"}</td>
                    </tr>
                    <tr>
                      <th>Balance Remaining</th>
                      <td>{formatMoney(summary?.remaining_balance)}</td>
                    </tr>
                    <tr>
                      <th>Current Estimate</th>
                      <td>{formatMoney(plan.estimated_amount ?? plan.base_fee)}</td>
                    </tr>
                    <tr className="payable-row">
                      <th>Payable Amount</th>
                      <td>{formatMoney(summary?.payable ?? plan.estimated_amount ?? plan.base_fee)}</td>
                    </tr>
                  </tbody>
                </table>

                <div className="upgrade-summary-actions">
                  <button
                    type="button"
                    className="upgrade-pay-btn"
                    onClick={handlePayNow}
                    disabled={submitting || plan.is_active}
                  >
                    {plan.is_active ? "Active Plan" : submitting ? "Please wait..." : "Pay Now"}
                  </button>
                  <button
                    type="button"
                    className="upgrade-back-btn"
                    onClick={() => navigate("/upgrade")}
                  >
                    Back
                  </button>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}

export default UpgradePlanDetails;
